import countryList from 'country-list';
import dayjs from 'dayjs';
import Config from 'utils/Config';
import strings from '../../common/Translation/Translate';
import { FormDataModel } from '../../components/Form';
import axiosInstance from '../axios';
import { clearStorage } from '../storage';
import { identifiers } from 'utils/constants/identifiers';
import { CardGiftcard, CheckCircle, Forum, HelpOutline, HighlightOff, HourglassBottom } from '@mui/icons-material';
import React from 'react';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Currency from 'utils/hooks/getCurrency';
import { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from '../../routes/AuthLayout/Sidebar';

export const calculateExpectedGuests = (
  guaranteedGuests: number | string | null | undefined,
  excessGuestsPercentage: number | null | undefined,
): number => {
  const guests = Number(guaranteedGuests);
  if (!Number.isFinite(guests) || guests < 0) {
    return 0;
  }
  const pct = Number(excessGuestsPercentage) || 0;
  return Math.round(guests * (1 + pct / 100));
};

export const formatGuestCountCompact = (
  guaranteedGuests: number | string | null | undefined,
  expectedGuests: number | string | null | undefined,
): string => {
  const guaranteed = Number(guaranteedGuests) || 0;
  const expected = Number(expectedGuests) || 0;
  if (expected > 0 && expected !== guaranteed) {
    return strings.formatString(strings.bookingDetail_guestCountsCompact, guaranteed, expected) as string;
  }
  return String(guaranteed);
};

export const buildExpectedGuestsBelowText = (
  expectedGuestsLabel: string,
  guaranteedGuests: number | string | null | undefined,
  excessGuestsPercentage: number | null | undefined,
): string => {
  const guests = Number(guaranteedGuests);
  const pct = Number(excessGuestsPercentage) || 0;
  if (!Number.isFinite(guests) || guests <= 0 || pct <= 0) {
    return '';
  }
  const expected = calculateExpectedGuests(guests, pct);
  return `${expectedGuestsLabel}: ${expected}`;
};

export const validateExpectedGuestsAgainstLayout = (
  guaranteedGuests: number | string | null | undefined,
  excessGuestsPercentage: number | null | undefined,
  layoutMaxCapacity: number | null | undefined,
  layoutName: string | null | undefined,
): { error: boolean; message: string } | false => {
  const max = Number(layoutMaxCapacity);
  if (!Number.isFinite(max) || max <= 0) return false;

  const guaranteed = Number(guaranteedGuests);
  if (!Number.isFinite(guaranteed) || guaranteed <= 0) return false;

  const expected = calculateExpectedGuests(guaranteed, excessGuestsPercentage);
  if (expected <= max) return false;

  const layoutLabel = layoutName?.trim() || strings.new_booking_laouts;
  return {
    error: true,
    message: strings.formatString(strings.layout_max_guests_validation, layoutLabel, String(max)) as string,
  };
};

export const validateExpectedGuestsAgainstVenue = (
  guaranteedGuests: number | string | null | undefined,
  excessGuestsPercentage: number | null | undefined,
  venueMaxCapacity: number | null | undefined,
  venueName: string | null | undefined,
): { error: boolean; message: string } | false => {
  const max = Number(venueMaxCapacity);
  if (!Number.isFinite(max) || max <= 0) return false;

  const guaranteed = Number(guaranteedGuests);
  if (!Number.isFinite(guaranteed) || guaranteed <= 0) return false;

  const expected = calculateExpectedGuests(guaranteed, excessGuestsPercentage);
  if (expected <= max) return false;

  const venueLabel = venueName?.trim() || strings.new_booking_venue;
  return {
    error: true,
    message: strings.formatString(strings.venue_max_guests_validation, venueLabel, String(max)) as string,
  };
};

/** Validates expected guests against venue capacity first, then layout when a layout is selected. */
export const validateExpectedGuestsCapacity = (
  guaranteedGuests: number | string | null | undefined,
  excessGuestsPercentage: number | null | undefined,
  venueMaxCapacity: number | null | undefined,
  venueName: string | null | undefined,
  layoutMaxCapacity: number | null | undefined,
  layoutName: string | null | undefined,
  layoutSelected: boolean,
): { error: boolean; message: string } | false => {
  const venueError = validateExpectedGuestsAgainstVenue(
    guaranteedGuests,
    excessGuestsPercentage,
    venueMaxCapacity,
    venueName,
  );
  if (venueError) return venueError;

  if (layoutSelected) {
    return validateExpectedGuestsAgainstLayout(
      guaranteedGuests,
      excessGuestsPercentage,
      layoutMaxCapacity,
      layoutName,
    );
  }

  return false;
};

export const determinePartOfDay = (timeValue: string | null | undefined): string => {
  if (!timeValue || typeof timeValue !== 'string') return '';
  const match = timeValue.match(/^(\d{2}):(\d{2})/);
  if (!match) return '';
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const totalMinutes = hours * 60 + minutes;
  if (totalMinutes >= 0 && totalMinutes < 6 * 60) return 'DINNER';
  if (totalMinutes >= 6 * 60 && totalMinutes < 10 * 60) return 'BREAKFAST';
  if (totalMinutes >= 10 * 60 && totalMinutes < 15 * 60) return 'LUNCH';
  if (totalMinutes >= 15 * 60 && totalMinutes < 18 * 60) return 'HI_TEA';
  if (totalMinutes >= 18 * 60 && totalMinutes < 24 * 60) return 'DINNER';
  return '';
};

export const getFormFields: (formData: FormDataModel) => {
  [p: string]: string | number | boolean;
} = (formData: FormDataModel) => {
  const fields: { [key: string]: string | number | boolean | any } = {};
  for (const key in formData) {
    if (!key.includes('Error')) {
      fields[key] = formData[key];
    }
  }
  return fields;
};

export const removeErrorFieldsFromValues = (formData: FormDataModel) => {
  const fields: FormDataModel = {};
  for (const key in formData) {
    if (!key.includes('Error')) {
      fields[key] = formData[key];
    }
  }
  return fields;
};
export const fetchErrorFields = (formData: FormDataModel, formModel?: any) => {
  const fields = [] as any;
  for (const key in formData) {
    if (key.includes('Error') && formData[key]) {
      formModel.forEach((form: any) => {
        if (form.field === key.replace('Error', '')) {
          fields.push(form.label);
        }
      });
    }
  }
  return fields;
};

export const createDropdownData = (data: any = [], keys: any = []) => {
  const createdArray: { value: any; label: any }[] = [];
  if (data && data.length) {
    data.forEach((item: any) => {
      createdArray.push({
        value: item[keys[0]],
        label: item[keys[1]],
      });
    });
  }
  return createdArray;
};

export type LayoutDropdownOption = {
  value: any;
  label: string;
  minCapacity?: number | null;
  maxCapacity?: number | null;
};

export type PricePackageDropdownOption = {
  value: any;
  label: string;
  pricePerPerson?: number | null;
};

export type VenueDropdownOption = {
  value: any;
  label: string;
  maxCapacity?: number | null;
};

export type VenueEventCapacity = {
  venueId: number | string;
  eventTypeId: number | string;
  maximumBookCapacity: number;
};

export const buildVenuesForEventType = (
  allVenues: { value: any; label: string }[],
  capacities: VenueEventCapacity[] | undefined,
  eventTypeId: string | number | null | undefined,
): VenueDropdownOption[] => {
  if (!allVenues?.length) return [];
  if (!eventTypeId || !capacities?.length) {
    return allVenues.map(v => ({ ...v, maxCapacity: null }));
  }

  const capacityByVenue = new Map<string, number>(
    capacities
      .filter(c => String(c.eventTypeId) === String(eventTypeId))
      .map(c => [String(c.venueId), c.maximumBookCapacity]),
  );

  return allVenues
    .filter(v => capacityByVenue.has(String(v.value)))
    .map(v => ({
      ...v,
      maxCapacity: capacityByVenue.get(String(v.value)) ?? null,
    }));
};

export const createLayoutDropdownData = (data: any[] = []): LayoutDropdownOption[] => {
  if (!data?.length) return [];
  return data.map(item => ({
    value: item.id,
    label: item.name,
    minCapacity: item.minCapacity ?? 0,
    maxCapacity: item.maxCapacity ?? null,
  }));
};

export const createPricePackageDropdownData = (data: any[] = []): PricePackageDropdownOption[] => {
  if (!data?.length) return [];
  return data.map(item => ({
    value: item.id ?? item.value,
    label: item.name ?? item.label,
    pricePerPerson: item.pricePerPerson ?? null,
  }));
};

export const getLayoutMaxCapacity = (
  layouts: LayoutDropdownOption[] | undefined,
  layoutId: unknown,
): number | null => {
  if (layoutId === '' || layoutId == null || !layouts?.length) return null;
  const match = layouts.find(l => String(l.value) === String(layoutId));
  return match?.maxCapacity != null ? Number(match.maxCapacity) : null;
};

export const venueDependentDropdownHints = (venueSelected: boolean, strings: any) =>
  venueSelected
    ? {}
    : {
        noOptionsText: strings.new_booking_select_venue_first,
        placeholder: strings.new_booking_select_venue_first,
      };

export const formatCurrency = (amount: any) => {
  return Currency(amount);
};

export const fetchIdsFromSelectedDropdownValues = (values: { value?: unknown }[] = []) => {
  const extractedValue: any[] = [];
  if (values && values.length) {
    values.forEach((item: any) => {
      extractedValue.push(item.value);
    });
  }
  return extractedValue;
};
export const fetchLabelsFromSelectedDropdownValues = (values: { label?: unknown }[] = []) => {
  const extractedValue: any[] = [];
  if (values && values.length) {
    values.forEach((item: any) => {
      extractedValue.push(item.label);
    });
  }
  return extractedValue.join(',');
};
export const logoutUser = () => {
  clearStorage('all');
  window.location.href = '#/login';
};
export const downloadExportFile = (response: any, type: string, fileName: string) => {
  const url = window.URL.createObjectURL(new Blob([response.data || response]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('target', `_blank`);

  if (!fileName.toLowerCase().endsWith(`.${type}`)) {
    fileName += `.${type}`;
  }

  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
};

export const ScrollTop = (x: number) => {
  window.scrollTo({
    top: x,
    behavior: 'smooth',
  });
};

export const BookingStatsConfig = (
  values: string,
): {
  label: string;
  IconComponent: React.ElementType;
  iconStyles: Record<string, any>;
} => {
  switch (values) {
    case 'CONFIRMED':
      return {
        label: strings.reports_text_confirmed_bookings,
        IconComponent: CheckCircle,
        iconStyles: { backgroundColor: '#E6F4EA', color: '#188038' },
      };
    case 'TENTATIVE':
      return {
        label: strings.reports_text_tentative_bookings,
        IconComponent: HelpOutline,
        iconStyles: { backgroundColor: '#FFF4E5', color: '#D17A00' },
      };
    case 'WAITLISTED':
      return {
        label: strings.reports_text_waitlisted_bookings,
        IconComponent: HourglassBottom,
        iconStyles: { backgroundColor: '#E8F0FE', color: '#1967D2' },
      };
    case 'CANCELLED':
      return {
        label: strings.reports_text_cancelled_bookings,
        IconComponent: HighlightOff,
        iconStyles: { backgroundColor: '#FDECEA', color: '#D93025' },
      };
    case 'COMPLEMENTARY':
      return {
        label: strings.reports_text_complementary_bookings,
        IconComponent: CardGiftcard,
        iconStyles: { backgroundColor: '#E0F7FA', color: '#00838F' },
      };
    case 'RFQ':
      return {
        label: strings.reports_text_rfq,
        IconComponent: Forum,
        iconStyles: { backgroundColor: '#FFF3E0', color: '#FB8C00' },
      };
    case 'PAYMENT_DUE':
      return {
        label: strings.reports_text_payment_due_bookings,
        IconComponent: AttachMoneyIcon,
        iconStyles: { backgroundColor: '#FDECEA', color: '#D93025' },
      };
    default:
      return {
        label: strings.reports_text_payment_due_bookings,
        IconComponent: AttachMoneyIcon,
        iconStyles: { backgroundColor: '#FDECEA', color: '#D93025' },
      };
  }
};

export const getBookingTypes = (value: any) => {
  let label = '';
  switch (value) {
    case 'CONFIRMED':
      label = strings.reports_text_confirmed_bookings;
      break;
    case 'TENTATIVE':
      label = strings.reports_text_tentative_bookings;
      break;
    case 'WAITLISTED':
      label = strings.reports_text_waitlisted_bookings;
      break;
    case 'CANCELLED':
      label = strings.reports_text_cancelled_bookings;
      break;
    case 'PAYMENT_DUE':
      label = strings.reports_text_payment_due_bookings;
      break;
    case 'RFQ':
      label = strings.reports_text_rfq;
      break;
    case 'COMPLEMENTARY':
      label = strings.reports_text_complementary_bookings;
      break;
    default:
  }
  return label;
};

export const getBookingTypeValue = (value: any) => {
  let val = '';
  switch (value) {
    case 'CONFIRMED':
      val = 'confirmedBookings';
      break;
    case 'TENTATIVE':
      val = 'tentativeBookings';
      break;
    case 'WAITLISTED':
      val = 'waitlistedBookings';
      break;
    case 'CANCELLED':
      val = 'cancelledBookings';
      break;
    case 'PAYMENT_DUE':
      val = 'paymentDues';
      break;
    case 'RFQ':
      val = 'rfqs';
      break;
    default:
  }
  return val;
};

export const getListData = async (setLoading: any, body: any, listApi: any, setPageResponse: any) => {
  try {
    setLoading(true);
    const { data } = await axiosInstance.post(listApi, body);
    setLoading(false);
    setPageResponse(data);
  } catch (error: any) {
    setLoading(false);
    setPageResponse(error);
  }
};

export const getPageDataWithLanguage = async (
  languageList: any,
  getByIdApi: any,
  id: any,
  setPageResponse: any,
  setLoading: any,
) => {
  try {
    setLoading(true);
    const { data } = await axiosInstance.get(getByIdApi(id));
    const container: any = {};
    for (let i = 0; i < languageList?.length; ++i) {
      let values = ['name_' + languageList[i]?.value];
      container[values as any] = data.localizedAttributes[languageList[i]?.value]
        ? data.localizedAttributes[languageList[i]?.value].name
        : '';
    }
    setPageResponse(container);
  } catch (error: any) {
  } finally {
    setLoading(false);
  }
};

export const getDrawerWidth = (screenSizeDownSm: any, screenSizeDownMd: any, screenSizeUpLg?: any) => {
  if (screenSizeDownSm) return '100%';
  else if (screenSizeDownMd) return '70%';
  else if (screenSizeUpLg) return '30%';
  else return '50%';
};
export const getRoomsDrawerWidth = (screenSizeDownSm: any, screenSizeDownMd: any, screenSizeUpLg?: any) => {
  if (screenSizeDownSm) return '100%';
  else if (screenSizeDownMd) return '90%';
  // else if (screenSizeUpLg) return '30%';
  else return '83%';
};

/** Full-width line-item drawer: spans main content area (viewport minus app sidebar). */
export const getLineItemDrawerWidth = (
  screenSizeDownSm: boolean,
  _screenSizeDownMd?: boolean,
  sidebarCollapsed = false,
) => {
  if (screenSizeDownSm) return '100%';
  const sidebarWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;
  return `calc(100vw - ${sidebarWidth}px)`;
};

export const getNewBookingSendEmailDrawerWidth = (
  screenSizeDownSm: any,
  screenSizeDownMd: any,
  screenSizeUpLg?: any,
) => {
  if (screenSizeDownSm) return '100%';
  if (screenSizeDownMd) return '420px';
  return '400px';
};

/** Earliest selectable booking date based on org {@code PAST_BOOKING_WINDOW} master data. */
export const getMinBookingDate = (pastBookingWindowDays?: number | null) => {
  const today = dayjs().startOf('day');
  if (pastBookingWindowDays != null && pastBookingWindowDays > 0) {
    return today.subtract(pastBookingWindowDays, 'day');
  }
  return today;
};

export const getBaseUrl = () => Config.BaseURL;

export const getCountryNameByCountryCode = (countryCode: any) => {
  if (countryCode === 'AC') return 'Ascension Island';
  if (countryCode === 'TA') return 'Tristan da Cunha';
  if (countryCode === 'XK') return 'Kosovo';
  return countryList.getName(countryCode);
};

export const getBorderColor = (index: any) => {
  const val5 = index === 4 ? '#9828a6' : '#333';
  const val4 = index === 4 ? '#f58442' : val5;
  const val3 = index === 3 ? '#ff0000' : val4;
  const val2 = index === 2 ? '#36a1ea' : val3;
  const val1 = index === 1 ? '#ffce56' : val2;
  return index === 0 ? '#50cc9e' : val1;
};

export const getColor = (upcoming: any) => {
  const val2 = upcoming.status === 'WAITLISTED' ? 'info' : 'error';
  const val1 = upcoming.status === 'TENTATIVE' ? 'warning' : val2;
  return upcoming.status === 'CONFIRMED' ? 'success' : val1;
};
export const getLabel = (upcoming: any) => {
  if (upcoming.status === 'WAITLISTED') return strings.WAITLISTED;
  else if (upcoming.status === 'TENTATIVE') return strings.TENTATIVE;
  else if (upcoming.status === 'CONFIRMED') return strings.CONFIRMED;
};

export const getMessage = (error: any) => {
  console.log(error.message, 'error');
  debugger;
  if (error.response) {
    const a = error.response.data?.error_description
      ? error.response.data.error_description
      : error.response.data.message || error.message;
    return a;
  } else {
    return error.message;
  }
};

export const publishNotification = (title: string, body: any, icon: any) => {
  console.log('Inside publishNotification', Notification.permission === 'granted');
  if (Notification.permission === 'granted') {
    createNotification(title, body, icon);
  } else {
    Notification.requestPermission(permission => {
      if (permission === 'granted') {
        createNotification(title, body, icon);
      }
    });
  }
};

const createNotification = (title: string, text: string, icon: string) => {
  new Notification(title, {
    body: text,
    icon,
  });
};

export const getFileName = (type: string) => {
  if (type === identifiers.CONTRACT) return strings.contract;
  if (type === identifiers.PROPOSAL) return strings.proposal;
  if (type === identifiers.PI) return strings.pi;
  if (type === identifiers.ENQUIRY) return strings.enquiry;
  return '';
};

export const getTimePeriodOptions = () => {
  return [
    { label: 'All Time', value: '-' },
    { label: 'Today', value: '0' },
    { label: 'This Week', value: '7' },
    { label: 'This Month', value: '30' },
  ];
};

export const isEmpty = (value: any) => {
  return value === null || value === undefined || value === '';
};
